import { IconButton, Tooltip, useTheme } from "@mui/material";
import { keyframes } from "@emotion/react";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import React from "react";
import { t } from "ttag";
import { useStoreActions } from "../../models/RootStore";

const connectedPulse = keyframes`
  0% { box-shadow: 0 0 0 0 color-mix(in srgb, currentColor 45%, transparent); }
  70% { box-shadow: 0 0 0 9px color-mix(in srgb, currentColor 0%, transparent); }
  100% { box-shadow: 0 0 0 0 color-mix(in srgb, currentColor 0%, transparent); }
`;

export type MockDeviceStateField = "connected" | "registered" | "blocked" | "proLicense";

const FIELD_ICON: Partial<Record<MockDeviceStateField, React.ElementType>> = {
  connected: PowerSettingsNewIcon,
  registered: LockIcon,
  proLicense: BusinessCenterIcon,
};

export type RegistrationState = "unregistered" | "real" | "fake";

const getRegistrationState = (device: any): RegistrationState => {
  if (!device.registered) {
    return "unregistered";
  }

  return device.registeredSiteId ? "real" : "fake";
};

const getTooltipText = (field: MockDeviceStateField, isOn: boolean) => {
  switch (field) {
    case "connected":
      return isOn ? t`Connected — click to disconnect` : t`Disconnected — click to connect`;
    case "blocked":
      return isOn ? t`Blocked — click to unblock` : t`Not blocked — click to block`;
    case "proLicense":
      return isOn ? t`Pro license active — click to remove` : t`No Pro license — click to grant`;
    default:
      return "";
  }
};

const MockDeviceStateSwitches = ({ device, field, disabled, disabledReason, onRequestUnregisterConfirm }: {
  device: any;
  field: MockDeviceStateField;
  disabled?: boolean;
  disabledReason?: string;
  onRequestUnregisterConfirm?: (device: any) => void;
}) => {
  const theme: any = useTheme();
  const setMockDeviceConnection = useStoreActions((actions) => actions.devices.setMockDeviceConnection);
  const setMockDeviceRegistration = useStoreActions((actions) => actions.devices.setMockDeviceRegistration);
  const setMockDeviceBlocked = useStoreActions((actions) => actions.devices.setMockDeviceBlocked);
  const setMockDeviceProLicense = useStoreActions((actions) => actions.devices.setMockDeviceProLicense);

  const isOn = !!device[field];
  const registrationState = field === "registered" ? getRegistrationState(device) : undefined;

  const handleClick = () => {
    if (field === "registered" && registrationState === "real") {
      if (onRequestUnregisterConfirm) {
        onRequestUnregisterConfirm(device);
      } else {
        setMockDeviceRegistration({ id: device.id, registered: false });
      }
      return;
    }

    const nextValue = !isOn;

    if (field === "connected") {
      setMockDeviceConnection({ id: device.id, connected: nextValue });
    } else if (field === "registered") {
      setMockDeviceRegistration({ id: device.id, registered: nextValue });
    } else if (field === "blocked") {
      setMockDeviceBlocked({ id: device.id, blocked: nextValue });
    } else {
      setMockDeviceProLicense({ id: device.id, proLicense: nextValue });
    }
  };

  const onColor = theme.palette.primary.main;
  const offColor = theme.palette.action.disabled;

  const IconComponent = field === "blocked"
    ? (isOn ? BlockIcon : CheckCircleIcon)
    : FIELD_ICON[field];

  const registeredColor = registrationState === "real"
    ? theme.palette.error.main
    : registrationState === "fake"
      ? theme.palette.success.main
      : offColor;

  const iconColor = field === "blocked"
    ? (isOn ? theme.palette.error.main : theme.palette.success.main)
    : field === "registered"
      ? registeredColor
      : (isOn ? onColor : offColor);

  const tooltipText = disabled && disabledReason
    ? disabledReason
    : field === "registered"
      ? (registrationState === "real"
        ? t`Registered to ${device.registeredSiteName} — click to unregister`
        : registrationState === "fake"
          ? t`Registered (no real site attached) — click to unregister`
          : t`Not registered — click to register`)
      : getTooltipText(field, isOn);

  const isConnectedOn = field === "connected" && isOn;

  const buttonSx = isConnectedOn
    ? {
      color: "#fff",
      backgroundColor: onColor,
      "&:hover": { backgroundColor: onColor },
      animation: `${connectedPulse} 1.6s ease-out infinite`,
    }
    : { color: iconColor };

  return (
    <Tooltip title={tooltipText}>
      <span>
        <IconButton onClick={handleClick} disabled={disabled} sx={buttonSx}>
          {IconComponent && <IconComponent />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default MockDeviceStateSwitches;
